/**
 * OpportunityCalculatorV2 — v2.3 (2026-05-07).
 *
 * Checkpoint calculator: 22 questions across 10 sections.
 * Quick Score mode: 7 questions / ~105 sec.
 *
 * Spec:
 *   - Progress indicator: "Question N of TOTAL · SECTION NAME" + 4px red bar
 *   - 5-point graded scale + skip link (null = excluded from denominator)
 *   - Left-border color per answer option
 *   - Composite: round((sum_points / (4 × count_answered)) × 100)
 *   - Confidence tag: high ≥80%, medium 50–79%, low <50%
 *   - RAG: 70+ green, 50–69 amber, <50 red
 *   - Verdict: go-forward / work-the-gaps / cut-bait
 *   - Per-section breakdown table: section | coverage% | score
 *   - AIP props: accountName, onSaveAccount, inAip
 *
 * Manufacturing-only. M-PACT with hyphen. No emojis. No exclamation points.
 */

import { useMemo, useState } from "react";
import {
  QUESTIONS,
  QUICK_QUESTIONS,
  GRADED_OPTIONS,
  computeScore,
  sectionFor,
  SECTION_STRENGTH_LINES,
  SECTION_RISK_LINES,
  type Answers,
  type CalculatorResult,
  type Question,
  type SectionId,
} from "@shared/calculator-questions";
import { getHealthColor, getForecastCategory } from "@shared/schema";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const BG = "#FAFAF7";
const SLATE_BORDER = "#E5E5E7";
const SLATE_TEXT = "#525258";
const SLATE_MUTED = "#8E8E94";

type Mode = "FULL" | "QUICK";

interface Props {
  accountName?: string | null;
  onSaveAccount?: (result: CalculatorResult, mode: Mode) => void;
  inAip?: boolean;
  /** When true, suppress the mode-select and force Quick Score only (marketing) */
  quickOnly?: boolean;
}

const STORAGE_KEY = "aip.calculator.runs.v2_2";

interface SavedRun {
  ts: number;
  accountName: string | null;
  mode: Mode;
  answers: Answers;
  composite: number;
  band: CalculatorResult["band"];
}

function persistRun(run: SavedRun) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list: SavedRun[] = raw ? JSON.parse(raw) : [];
    list.unshift(run);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, 50)));
  } catch { /* localStorage unavailable — ignore */ }
}

export default function OpportunityCalculatorV2({
  accountName = null,
  onSaveAccount,
  inAip = false,
  quickOnly = false,
}: Props) {
  const [mode, setMode] = useState<Mode | null>(quickOnly ? "QUICK" : null);
  // quickOnly: show a dedicated start screen before launching questions
  const [quickOnlyStarted, setQuickOnlyStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [submitted, setSubmitted] = useState(false);
  const [savedAccountName, setSavedAccountName] = useState<string | null>(null);

  const activeQuestions: Question[] = useMemo(
    () => (mode === "QUICK" ? QUICK_QUESTIONS : QUESTIONS),
    [mode],
  );

  const totalCount = activeQuestions.length;
  const currentQ = activeQuestions[stepIndex] ?? null;

  const sectionsInRun = useMemo(() => {
    const seen = new Set<SectionId>();
    const out: SectionId[] = [];
    for (const q of activeQuestions) {
      if (!seen.has(q.section)) { seen.add(q.section); out.push(q.section); }
    }
    return out;
  }, [activeQuestions]);

  const currentSectionIdx = currentQ ? sectionsInRun.indexOf(currentQ.section) : -1;

  const result = useMemo(
    () => computeScore(answers, activeQuestions),
    [answers, activeQuestions],
  );

  function pickMode(m: Mode) {
    setMode(m);
    setStepIndex(0);
    setAnswers({});
    setSubmitted(false);
    setSavedAccountName(null);
  }

  function pick(qid: string, value: number) {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
    window.setTimeout(() => {
      setStepIndex((s) => {
        if (s + 1 >= totalCount) { setSubmitted(true); return s; }
        return s + 1;
      });
    }, 220);
  }

  function skip(qid: string) {
    setAnswers((prev) => ({ ...prev, [qid]: null }));
    window.setTimeout(() => {
      setStepIndex((s) => {
        if (s + 1 >= totalCount) { setSubmitted(true); return s; }
        return s + 1;
      });
    }, 100);
  }

  function back() {
    setStepIndex((s) => Math.max(0, s - 1));
    setSubmitted(false);
  }

  function reset() {
    if (quickOnly) {
      setMode("QUICK");
      setQuickOnlyStarted(false);
      setStepIndex(0);
      setAnswers({});
      setSubmitted(false);
      setSavedAccountName(null);
    } else {
      setMode(null);
      setStepIndex(0);
      setAnswers({});
      setSubmitted(false);
      setSavedAccountName(null);
    }
  }

  function handleSaveAccount() {
    if (!onSaveAccount || !mode) return;
    onSaveAccount(result, mode);
    persistRun({ ts: Date.now(), accountName: accountName ?? null, mode, answers, composite: result.composite, band: result.band });
    setSavedAccountName(accountName ?? "account");
  }

  function handleEmail() {
    if (typeof window === "undefined") return;
    const subject = encodeURIComponent(
      `Opportunity checkpoint audit${accountName ? ` — ${accountName}` : ""}: ${result.composite}/100`,
    );
    const lines = [
      `Composite: ${result.composite}/100 (${result.band})`,
      `Verdict: ${result.verdictLine}`,
      `Confidence: ${result.answeredCount} of ${result.totalCount} answered (${result.confidenceTag})`,
      "",
      "Per-section:",
      ...result.sections.map(
        (s) => `  ${s.title}: ${s.pct !== null ? `${s.pct}/100` : "—"} (${s.answered}/${s.total} answered)`,
      ),
    ];
    const body = encodeURIComponent(lines.join("\n"));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  // ── QUICK-ONLY START SCREEN ────────────────────────────────────────────────
  if (quickOnly && !quickOnlyStarted) {
    return (
      <div
        style={{ background: "#FFFFFF", border: `1px solid ${SLATE_BORDER}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.12)" }}
        data-testid="calculator-quickonly-start"
      >
        <div style={{ padding: "28px 28px 8px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.28em", color: RED, textTransform: "uppercase", marginBottom: 10 }}>
            Checkpoint calculator
          </p>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: BLACK, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0, marginBottom: 8 }}>
            Opportunity Calculator
          </h2>
          <p style={{ fontSize: 14.5, color: SLATE_TEXT, lineHeight: 1.55, maxWidth: 720, margin: 0 }}>
            Seven checkpoint questions. Under two minutes. Get a composite 0-100 score and a plain go/no-go verdict.
            {accountName ? (
              <> Scoring against <strong style={{ color: BLACK }}>{accountName}</strong>.</>
            ) : null}
          </p>
        </div>

        <div style={{ padding: "24px 28px 32px", marginTop: 8 }}>
          <p style={{ fontSize: 12, color: SLATE_TEXT, margin: "0 0 6px", letterSpacing: "0.01em" }}>
            Click to start
          </p>
          <button
            type="button"
            onClick={() => setQuickOnlyStarted(true)}
            data-testid="quickonly-start-btn"
            style={{
              display: "block",
              width: "100%",
              padding: "12px 24px",
              background: RED,
              color: "#FFFFFF",
              border: "none",
              borderRadius: 9999,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              textAlign: "center",
              letterSpacing: "0.02em",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#b91c1c"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = RED; }}
          >
            Start Quick Score →
          </button>
          <p style={{ fontSize: 11.5, color: SLATE_TEXT, margin: "12px 0 0", lineHeight: 1.55 }}>
            7 questions · ~105 seconds · no login required · same scoring engine as the full 22-question checkpoint audit
          </p>
        </div>
      </div>
    );
  }

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (mode === null) {
    return (
      <div
        style={{ background: "#FFFFFF", border: `1px solid ${SLATE_BORDER}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.12)" }}
        data-testid="calculator-intro"
      >
        <div style={{ padding: "28px 28px 8px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.28em", color: RED, textTransform: "uppercase", marginBottom: 10 }}>
            Checkpoint calculator
          </p>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: BLACK, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0, marginBottom: 8 }}>
            Opportunity Calculator
          </h2>
          <p style={{ fontSize: 14.5, color: SLATE_TEXT, lineHeight: 1.55, maxWidth: 720, margin: 0 }}>
            Score a deal against the checkpoint questions that determine whether it is worth pursuing or you should cut bait now.
            {accountName ? (
              <> Scoring against <strong style={{ color: BLACK }}>{accountName}</strong>.</>
            ) : null}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, padding: "20px 28px 28px", marginTop: 8 }}>
          {/* Full checkpoint audit card */}
          <button
            type="button"
            onClick={() => pickMode("FULL")}
            data-testid="mode-full"
            style={{
              textAlign: "left",
              padding: "20px 20px 16px",
              background: "#FFFFFF",
              border: `2px solid ${SLATE_BORDER}`,
              borderRadius: 12,
              cursor: "pointer",
              transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.borderColor = RED;
              el.style.boxShadow = "0 4px 16px -4px rgba(220,38,38,0.18), 0 2px 6px -2px rgba(15,23,42,0.08)";
              el.style.background = BG;
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.borderColor = SLATE_BORDER;
              el.style.boxShadow = "none";
              el.style.background = "#FFFFFF";
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BLACK, textTransform: "uppercase" }}>Full checkpoint audit</span>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: SLATE_TEXT, textTransform: "uppercase" }}>22 q · 5 min</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: BLACK, lineHeight: 1.2, marginBottom: 8 }}>
              All 22 checkpoint questions across ten sections
            </div>
            <p style={{ fontSize: 13, color: SLATE_TEXT, lineHeight: 1.55, margin: "0 0 16px", flex: 1 }}>
              Budget, Authority, Need, Timeline, Compliance, Financial, Competitor, Organization, People Network, and Legal — Contracts, SLAs, Terms. Returns a composite score, per-section breakdown, and a written go/no-go verdict.
            </p>
            <p style={{ fontSize: 11.5, color: "#6B6B70", margin: "0 0 8px", letterSpacing: "0.01em" }}>
              Click to start
            </p>
            <div
              style={{
                display: "block",
                width: "100%",
                padding: "10px 16px",
                background: BLACK,
                color: "#FFFFFF",
                borderRadius: 9999,
                fontSize: 14,
                fontWeight: 600,
                textAlign: "center",
                letterSpacing: "0.02em",
                pointerEvents: "none",
              }}
            >
              Start full audit →
            </div>
          </button>

          {/* Quick Score card */}
          <button
            type="button"
            onClick={() => pickMode("QUICK")}
            data-testid="mode-quick"
            style={{
              textAlign: "left",
              padding: "20px 20px 16px",
              background: "#FFFFFF",
              border: `2px solid ${SLATE_BORDER}`,
              borderRadius: 12,
              cursor: "pointer",
              transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.borderColor = RED;
              el.style.boxShadow = "0 4px 16px -4px rgba(220,38,38,0.18), 0 2px 6px -2px rgba(15,23,42,0.08)";
              el.style.background = BG;
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              el.style.borderColor = SLATE_BORDER;
              el.style.boxShadow = "none";
              el.style.background = "#FFFFFF";
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: RED, textTransform: "uppercase" }}>Quick Score</span>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: SLATE_TEXT, textTransform: "uppercase" }}>7 q · ~105 sec</span>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: BLACK, lineHeight: 1.2, marginBottom: 8 }}>
              Directional read in under two minutes
            </div>
            <p style={{ fontSize: 13, color: SLATE_TEXT, lineHeight: 1.55, margin: "0 0 16px", flex: 1 }}>
              One checkpoint question each from Budget, Authority, Need, Timeline, Competitor, People Network, and Legal — Contracts, SLAs, Terms. Same scoring math. For sizing up a deal in the moment.
            </p>
            <p style={{ fontSize: 11.5, color: "#6B6B70", margin: "0 0 8px", letterSpacing: "0.01em" }}>
              Click to start
            </p>
            <div
              style={{
                display: "block",
                width: "100%",
                padding: "10px 16px",
                background: RED,
                color: "#FFFFFF",
                borderRadius: 9999,
                fontSize: 14,
                fontWeight: 600,
                textAlign: "center",
                letterSpacing: "0.02em",
                pointerEvents: "none",
              }}
            >
              Start Quick Score →
            </div>
          </button>
        </div>

        <div style={{ padding: "18px 28px 28px", borderTop: `1px solid ${SLATE_BORDER}` }}>
          <p style={{ fontSize: 12, color: SLATE_TEXT, margin: 0, lineHeight: 1.55 }}>
            22 checkpoint questions across 10 sections. Five-point graded scale — skip any question you cannot answer yet. Manufacturing-only: industrial, automotive, aerospace, building materials, industrial equipment, contract manufacturing.
          </p>
        </div>
      </div>
    );
  }

  // ── RESULT ─────────────────────────────────────────────────────────────────
  if (submitted || stepIndex >= totalCount) {
    // Canon §5 5-band palette driven by composite score, not the 3-band
    // GREEN/AMBER/RED legacy on result.band.
    const bandColor = getHealthColor(result.composite);
    const bandBg   = `${bandColor}1A`; // ~10% alpha tint
    const bandLabel = getForecastCategory(result.composite);

    return (
      <div
        style={{ background: "#FFFFFF", border: `1px solid ${SLATE_BORDER}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.12)" }}
        data-testid="calculator-result"
      >
        {/* Composite header */}
        <div style={{ padding: "28px", borderBottom: `1px solid ${SLATE_BORDER}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.28em", color: RED, textTransform: "uppercase", margin: 0, marginBottom: 8 }}>
                {mode === "QUICK" ? "Quick Score" : "Full checkpoint audit"}{accountName ? ` · ${accountName}` : ""}
              </p>
              <h3 style={{ fontSize: 26, fontWeight: 800, color: bandColor, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0, marginBottom: 6 }}>
                {result.verdictLine}
              </h3>
              <p style={{ fontSize: 13, color: SLATE_TEXT, lineHeight: 1.55, margin: 0 }}>
                {result.answeredCount} of {result.totalCount} answered &middot; <span style={{ fontWeight: 700 }}>{result.confidenceTag} confidence</span>
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 12, padding: "12px 18px", background: bandBg, borderRadius: 12, border: `2px solid ${bandColor}` }}>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", color: bandColor, textTransform: "uppercase" }}>{bandLabel}</div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: BLACK, lineHeight: 1, marginTop: 2 }}>
                    {result.composite}<span style={{ fontSize: 14, color: SLATE_TEXT, fontWeight: 600 }}> / 100</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Per-section breakdown */}
        <div style={{ padding: "24px 28px", borderBottom: `1px solid ${SLATE_BORDER}` }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BLACK, textTransform: "uppercase", margin: 0, marginBottom: 14 }}>
            Per-section breakdown
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", color: SLATE_MUTED, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8, paddingRight: 16 }}>Section</th>
                  <th style={{ textAlign: "right", color: SLATE_MUTED, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8, paddingRight: 16 }}>Answered</th>
                  <th style={{ textAlign: "right", color: SLATE_MUTED, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8 }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {result.sections.map((s) => {
                  const color = s.pct !== null ? getHealthColor(s.pct) : SLATE_MUTED;
                  return (
                    <tr key={s.id} data-testid={`section-${s.id}`} style={{ borderTop: `1px solid ${SLATE_BORDER}` }}>
                      <td style={{ paddingTop: 10, paddingBottom: 10, paddingRight: 16, color: BLACK, fontWeight: 600 }}>{s.title}</td>
                      <td style={{ paddingTop: 10, paddingBottom: 10, paddingRight: 16, textAlign: "right", color: SLATE_TEXT }}>
                        {s.answered}/{s.total}
                      </td>
                      <td style={{ paddingTop: 10, paddingBottom: 10, textAlign: "right", fontWeight: 700, color }}>
                        {s.pct !== null ? `${s.pct} / 100` : <span style={{ color: SLATE_MUTED, fontWeight: 400 }}>— not scored</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Commentary */}
        <div style={{ padding: "24px 28px", borderBottom: `1px solid ${SLATE_BORDER}`, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: "#15803D", textTransform: "uppercase", margin: 0, marginBottom: 10 }}>
              Strong checkpoints
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, color: BLACK, fontSize: 13.5, lineHeight: 1.6 }}>
              {result.strongest.length === 0 && <li style={{ color: SLATE_TEXT }}>Not enough answered yet.</li>}
              {result.strongest.map((id) => (
                <li key={id} style={{ marginBottom: 4 }}>{SECTION_STRENGTH_LINES[id]}</li>
              ))}
            </ul>
          </div>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: RED, textTransform: "uppercase", margin: 0, marginBottom: 10 }}>
              Gaps to close
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, color: BLACK, fontSize: 13.5, lineHeight: 1.6 }}>
              {result.weakest.length === 0 && <li style={{ color: SLATE_TEXT }}>Not enough answered yet.</li>}
              {result.weakest.map((id) => (
                <li key={id} style={{ marginBottom: 4 }}>{SECTION_RISK_LINES[id]}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding: "20px 28px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            onClick={reset}
            data-testid="result-reset"
            style={{ padding: "10px 18px", background: BLACK, color: "#FFFFFF", border: "none", borderRadius: 9999, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
          >
            Restart
          </button>
          {inAip && (
            <>
              <button
                type="button"
                onClick={handleEmail}
                data-testid="result-email"
                style={{ padding: "10px 18px", background: "#FFFFFF", color: BLACK, border: `1px solid ${BLACK}`, borderRadius: 9999, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
              >
                Email this score
              </button>
              {accountName && (
                <button
                  type="button"
                  onClick={handleSaveAccount}
                  data-testid="result-save-account"
                  disabled={!!savedAccountName}
                  style={{ padding: "10px 18px", background: savedAccountName ? "#15803D" : RED, color: "#FFFFFF", border: "none", borderRadius: 9999, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: savedAccountName ? "default" : "pointer" }}
                >
                  {savedAccountName ? `Saved to ${savedAccountName}` : `Save to ${accountName}`}
                </button>
              )}
            </>
          )}
          <span style={{ flex: 1 }} />
          {!inAip && mode === "QUICK" && (
            <span style={{ fontSize: 12, color: SLATE_TEXT, maxWidth: 340, lineHeight: 1.5 }}>
              Want the full 22-question checkpoint audit?{" "}
              <a href="/contact" style={{ color: RED, fontWeight: 700, textDecoration: "underline" }}>Sign the NDA</a>
              {" "}to unlock the complete report.
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── QUESTION ───────────────────────────────────────────────────────────────
  if (!currentQ) return null;
  const sec = sectionFor(currentQ.section);
  const selected = answers[currentQ.id];
  // Progress bar: N/TOTAL filled
  const barWidth = Math.round((stepIndex / totalCount) * 100);

  return (
    <div
      style={{ background: "#FFFFFF", border: `1px solid ${SLATE_BORDER}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.12)" }}
      data-testid="calculator-question"
    >
      {/* PROGRESS HEADER — "Question N of TOTAL · SECTION NAME" */}
      <header
        style={{ position: "sticky", top: 0, zIndex: 5, background: BLACK, color: "#FFFFFF" }}
        data-testid="calculator-progress-header"
      >
        {/* 4px progress bar at very top */}
        <div style={{ height: 4, background: "rgba(255,255,255,0.12)" }}>
          <div style={{ height: "100%", width: `${barWidth}%`, background: RED, transition: "width 0.35s ease" }} />
        </div>
        <div style={{ padding: "12px 24px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em" }}>
              Question {stepIndex + 1} of {totalCount}
            </span>
            <span style={{ fontSize: 12, color: "#8E8E94", margin: "0 10px" }}>·</span>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "#FCA5A5" }}>
              {sec.title.toUpperCase()}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#8E8E94", letterSpacing: "0.06em" }}>
            Section {currentSectionIdx + 1} of {sectionsInRun.length}
          </div>
        </div>
      </header>

      {/* Question body */}
      <div
        key={currentQ.id}
        style={{ padding: "26px 28px 18px", animation: "calc-slide-in 0.28s ease-out" }}
      >
        <style>{`
          @keyframes calc-slide-in {
            from { opacity: 0; transform: translateX(10px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        `}</style>

        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: SLATE_TEXT, textTransform: "uppercase", margin: 0, marginBottom: 6 }}>
          {sec.caption}
        </p>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: BLACK, lineHeight: 1.4, margin: 0, marginBottom: 20 }}>
          {currentQ.stem}
        </h3>

        {/* 5-point graded options */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {GRADED_OPTIONS.map((opt) => {
            const active = selected === opt.value;
            const pts = opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => pick(currentQ.id, opt.value)}
                data-testid={`option-${currentQ.id}-${opt.value}`}
                style={{
                  textAlign: "left",
                  padding: "11px 14px 11px 16px",
                  borderRadius: 8,
                  border: active ? `2px solid ${RED}` : `1px solid ${SLATE_BORDER}`,
                  borderLeft: `4px solid ${opt.leftBorderColor}`,
                  background: active ? "#FEF2F2" : "#FFFFFF",
                  color: BLACK,
                  fontSize: 15,
                  lineHeight: 1.45,
                  cursor: "pointer",
                  transition: "background 0.1s, border-color 0.1s",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
                onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = BG; }}
                onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = "#FFFFFF"; }}
              >
                <span>{opt.label}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: SLATE_MUTED, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {pts} {pts === 1 ? "pt" : "pts"}
                </span>
              </button>
            );
          })}
        </div>

        {/* Skip link */}
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <button
            type="button"
            onClick={() => skip(currentQ.id)}
            data-testid={`skip-${currentQ.id}`}
            style={{ background: "none", border: "none", color: SLATE_MUTED, fontSize: 12, cursor: "pointer", textDecoration: "underline", padding: "4px 8px" }}
          >
            Skip — unknown for now
          </button>
        </div>
      </div>

      {/* Footer nav */}
      <div style={{ padding: "12px 28px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderTop: `1px solid ${SLATE_BORDER}` }}>
        <button
          type="button"
          onClick={back}
          disabled={stepIndex === 0}
          data-testid="question-back"
          style={{
            padding: "8px 16px",
            background: "#FFFFFF",
            color: stepIndex === 0 ? "#C9C9CC" : BLACK,
            border: `1px solid ${stepIndex === 0 ? SLATE_BORDER : BLACK}`,
            borderRadius: 9999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            cursor: stepIndex === 0 ? "not-allowed" : "pointer",
          }}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={reset}
          data-testid="question-reset"
          style={{ padding: "8px 14px", background: "transparent", color: SLATE_TEXT, border: "none", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
        >
          Start over
        </button>
        <button
          type="button"
          onClick={() => {
            if (selected !== undefined) {
              if (stepIndex + 1 >= totalCount) setSubmitted(true);
              else setStepIndex(stepIndex + 1);
            }
          }}
          disabled={selected === undefined}
          data-testid="question-next"
          style={{
            padding: "8px 18px",
            background: selected === undefined ? "#FCA5A5" : RED,
            color: "#FFFFFF",
            border: "none",
            borderRadius: 9999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            cursor: selected === undefined ? "not-allowed" : "pointer",
            opacity: selected === undefined ? 0.7 : 1,
          }}
        >
          {stepIndex + 1 >= totalCount ? "See score →" : "Next →"}
        </button>
      </div>
    </div>
  );
}

// Re-export for callers
export type { CalculatorResult } from "@shared/calculator-questions";
